import React, { forwardRef, useImperativeHandle } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { Views } from 'react-big-calendar';
import ScheduleWeek from './ScheduleWeek';
import ScheduleHeader from './ScheduleHeader';
import { defaultDate, maxDate, minDate, date2WeekdayCd, ScheduleType, date2SaveTime } from '../../util/TimeTableUtil';
import './scss/index.scss';
import ScheduleModal from '../../view/modal/ScheduleModal';
import { modalType } from '../../util/ModalUtil';
import { useDispatch, useSelector } from 'react-redux';
import { actionEdit, actionSearch } from '../../module/component/ScheduleModule';
import AuthenticationUtil from '../../util/AuthenticationUtil';

moment.locale('ko-KR');
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

export let MINIMUN_GAP_OF_START_TO_END = {
    SLOTS: 7,
    MIILIS: 1800000,
};

const selectRangeFormat = ({ start, end }, culture, localizer) => {
    return localizer.format(start, 'hh:mm A', culture) + '~' + localizer.format(end, 'hh:mm A', culture);
};

export const checkTimeRange = (start, end, popupRef) => {
    if (start.getHours() > 22 || start.getHours() < 8) {
        popupRef.current.openAlert('일정 시작시간이 유효하지 않습니다. (유효시간 8시~22시)');
        return false;
    }
    if (end.getHours() > 22 || end.getHours() < 8) {
        popupRef.current.openAlert('일정 종료시간이 유효하지 않습니다. (유효시간 8시~22시)');
        return false;
    }
    if (start.getHours() > end.getHours()) {
        popupRef.current.openAlert('일정 시작시간은 종료시간보다 클 수 없습니다.');
        return false;
    } else {
        var startTime = parseInt(start.getTime()) + parseInt(MINIMUN_GAP_OF_START_TO_END.MIILIS);
        var endTime = parseInt(end.getTime());
        if (startTime > endTime) {
            popupRef.current.openAlert('일정 시작시간과 종료시간은 최소 30분 이상의 간격이 있어야 합니다.');
            return false;
        }
    }
    return true;
};

const Schedule = forwardRef(({ semesterId, userId, popupRef, modalRef }, ref) => {
    const dispatch = useDispatch();
    const { events } = useSelector((state) => state.scheduleState);

    useImperativeHandle(ref, () => ({
        search: (semesterId, userId) => {
            dispatch(actionSearch({ params: { semesterId, userId } }));
        },
    }));

    const handleModify = (data) => {
        const { start, end, event } = data;
        if (data.event.scheduleTypeCd != ScheduleType.CLASS) {
            if (checkTimeRange(start, end, popupRef)) {
                dispatch(
                    actionEdit({
                        data: {
                            uid: event.uid,
                            semesterId,
                            userId,
                            subjectNm: event.subjectNm,
                            weekdayCd: date2WeekdayCd(start),
                            bgnTime: date2SaveTime(start),
                            endTime: date2SaveTime(end),
                        },
                        callback: () => {
                            dispatch(actionSearch({ params: { semesterId } }));
                        },
                    }),
                );
            }
        }
    };

    const onSelectSlot = (data) => {
        const { start, end } = data;
        if (checkTimeRange(start, end, popupRef)) {
            modalRef.current.open(
                <ScheduleModal
                    type={modalType.ADD}
                    event={{ ...data, scheduleTypeCd: ScheduleType.PRIVATE, subjectNm: '', lessonUniversityCd: '' }}
                    popupRef={popupRef}
                    modalRef={modalRef}
                    semesterId={semesterId}
                    userId={userId}
                />,
                'large',
            );
        }
    };

    const onDoubleClickEvent = (event) => {
        if (event.scheduleTypeCd == ScheduleType.PRIVATE) {
            modalRef.current.open(
                <ScheduleModal
                    type={modalType.EDIT}
                    event={event}
                    popupRef={popupRef}
                    modalRef={modalRef}
                    semesterId={semesterId}
                    userId={userId}
                />,
                'large',
            );
        } else {
            modalRef.current.open(
                <ScheduleModal
                    type={modalType.DETAIL}
                    event={event}
                    popupRef={popupRef}
                    modalRef={modalRef}
                    semesterId={semesterId}
                    userId={userId}
                />,
                'large',
            );
        }
    };

    const onKeyPressEvent = (e) => {
        // TODO what?
    };

    const eventPropGetter = (event, start, end, isSelected) => {
        return event.scheduleTypeCd == ScheduleType.CLASS
            ? {
                  style: {
                      backgroundColor: 'rgba(51, 51, 51, 0.3)',
                      borderRadius: '4px',
                      border: '0px',
                      color: 'rgba(80, 80, 80, 1)',
                      display: 'block',
                      borderLeft: 'rgba(136, 136, 136, 1) solid',
                  },
              }
            : {
                  style: {
                      backgroundColor: 'rgba(14, 165, 233, 0.3)',
                      borderRadius: '4px',
                      border: '0px',
                      color: 'rgba(25, 105, 178, 1)',
                      display: 'block',
                      borderLeft: 'rgba(14, 165, 233, 1) solid',
                  },
              };
    };

    return (
        <div className='time-table'>
            <DnDCalendar
                defaultDate={defaultDate}
                defaultView={Views.WEEK}
                views={{ week: ScheduleWeek }}
                events={events}
                localizer={localizer}
                onEventDrop={handleModify}
                onEventResize={handleModify}
                resizable
                toolbar={false}
                min={minDate}
                max={maxDate}
                timeslots={12}
                step={5}
                selectable={true}
                onSelectSlot={onSelectSlot}
                onDoubleClickEvent={onDoubleClickEvent}
                onKeyPressEvent={onKeyPressEvent}
                components={{
                    week: {
                        header: ScheduleHeader,
                    },
                }}
                formats={{
                    eventTimeRangeFormat: selectRangeFormat,
                }}
                eventPropGetter={eventPropGetter}
                style={{ height: '150vh' }}
                tooltipAccessor={null}
            />
        </div>
    );
});

export default Schedule;
